import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Starfield from "@/components/Starfield";
import Rocket from "@/components/Rocket";
import Planet from "@/components/Planet";
import ProgressTrack from "@/components/ProgressTrack";
import TeacherTools from "@/components/TeacherTools";
import { fetchClass, updateClass, updateProgress, applyProgress, setActiveProject, addProject, createTeacherInvite, removeTeacher } from "@/lib/api";
import { fireCelebration, playChime } from "@/lib/celebrate";
import { JOIN_HOST, joinUrl } from "@/constants";
import { TimelineDialog, TrackerDialog, ClassReportDialog } from "@/components/ProjectViews";
import ClockDock from "@/components/ClockDock";
import MissionTimer from "@/components/MissionTimer";
import Whiteboard from "@/components/Whiteboard";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Maximize,
  Minimize,
  Volume2,
  VolumeX,
  Settings2,
  Wifi,
  WifiOff,
  History as HistoryIcon,
  CalendarDays,
  Share2,
  Copy,
  FolderPlus,
  CalendarClock,
  ListChecks,
  FileText,
  Wrench,
  Timer,
  PenTool,
  UserPlus,
  UserMinus,
  Users,
} from "lucide-react";

// A single pupil's journey lane: name + reusable draggable/tappable track.
function PupilLane({ pupil, stages, onSnap, onOpenHistory }) {
  return (
    <div data-testid={`pupil-row-${pupil.id}`} className="flex items-center gap-3 sm:gap-4">
      <button
        type="button"
        data-testid={`pupil-history-btn-${pupil.id}`}
        onClick={() => onOpenHistory(pupil)}
        className="w-24 sm:w-36 shrink-0 text-right group/name cursor-pointer"
        title="View progress history"
      >
        <span className="font-heading font-bold text-white text-sm sm:text-lg tracking-wide truncate flex items-center justify-end gap-1 group-hover/name:text-cyan-300 transition-colors">
          {pupil.isTeacher && <span title="Teacher pace-setter">👑</span>}
          {pupil.name}
          <HistoryIcon className="w-3.5 h-3.5 opacity-0 group-hover/name:opacity-70 shrink-0" />
        </span>
        <span className={`text-[10px] sm:text-xs truncate block ${pupil.isTeacher ? "text-amber-300" : "text-slate-400"}`}>
          {pupil.isTeacher ? "Expected pace" : stages[pupil.stageIndex]?.name}
        </span>
      </button>
      <ProgressTrack pupil={pupil} stages={stages} onSnap={onSnap} shipSize={60} />
    </div>
  );
}

export default function Smartboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [room, setRoom] = useState(null);
  const [offline, setOffline] = useState(false);
  const [muted, setMuted] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [tools, setTools] = useState(false);
  const [banner, setBanner] = useState(null);
  const [classBanner, setClassBanner] = useState(null);
  const [historyPupil, setHistoryPupil] = useState(null);
  const [showLaunch, setShowLaunch] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [trackerOpen, setTrackerOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [timerOpen, setTimerOpen] = useState(false);
  const [whiteboardOpen, setWhiteboardOpen] = useState(false);
  const [techOpen, setTechOpen] = useState(false);
  const [inviteLink, setInviteLink] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [teachersOpen, setTeachersOpen] = useState(false);
  const celebratedRef = useRef(null);
  const projRef = useRef(null);
  const pointerDownRef = useRef(false);
  const containerRef = useRef(null);

  useEffect(() => {
    (async () => {
      const res = await fetchClass(id);
      if (res.authRequired) {
        toast.error("Please sign in as a teacher to open this board.");
        navigate("/");
        return;
      }
      if (!res.data) {
        toast.error("Class not found");
        navigate("/");
        return;
      }
      setRoom(res.data);
      setOffline(res.offline);
    })();
  }, [id, navigate]);

  const removeCoTeacher = async (teacherId) => {
    setRoom((rm) => ({ ...rm, teachers: (rm.teachers || []).filter((t) => t.id !== teacherId) }));
    try {
      await removeTeacher(id, teacherId);
      const res = await fetchClass(id);
      if (res.data) setRoom(res.data);
      toast.success("Co-teacher removed");
    } catch (e) {
      const res = await fetchClass(id);
      if (res.data) setRoom(res.data);
      toast.error(e?.response?.status === 400 ? "The main teacher can't be removed" : "Could not remove co-teacher");
    }
  };

  const onSnap = async (pupil, stageIndex) => {
    if (stageIndex === pupil.stageIndex) return;
    const advanced = stageIndex > pupil.stageIndex;
    setRoom((r) => ({
      ...r,
      pupils: r.pupils.map((p) => (p.id === pupil.id ? applyProgress(p, stageIndex) : p)),
    }));
    const { offline } = await updateProgress(id, pupil.id, stageIndex);
    setOffline(offline);

    if (advanced) {
      const isFinish = stageIndex === room.stages.length - 1;
      fireCelebration(isFinish);
      playChime(muted);
      setBanner({
        name: pupil.name,
        stage: room.stages[stageIndex],
        finish: isFinish,
        color: pupil.color,
      });
      setTimeout(() => setBanner(null), 2800);
    }
  };

  const saveRoom = async (updated) => {
    setRoom(updated);
    const { offline } = await updateClass(id, {
      name: updated.name,
      stages: updated.stages,
      pupils: updated.pupils,
    });
    setOffline(offline);
  };

  const switchProject = async (pid) => {
    const d = await setActiveProject(id, pid);
    setRoom(d);
    setOffline(false);
  };
  const newProject = async () => {
    const nm = `Project ${(room.projects?.length || 0) + 1}`;
    const d = await addProject(id, nm);
    const np = d.projects[d.projects.length - 1];
    const d2 = await setActiveProject(id, np.id);
    setRoom(d2);
    toast.success("New project added 🚀");
  };
  const genInvite = async () => {
    setInviteBusy(true);
    try {
      const { token } = await createTeacherInvite(id);
      const link = `${window.location.origin}/teacher-invite/${token}`;
      setInviteLink(link);
      navigator.clipboard?.writeText(link);
      toast.success("One-time co-teacher invite link copied!");
    } catch {
      toast.error("Could not create invite");
    } finally {
      setInviteBusy(false);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
      setFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setFullscreen(false);
    }
  };

  useEffect(() => {
    const h = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", h);
    return () => document.removeEventListener("fullscreenchange", h);
  }, []);

  // Rocket lift-off intro when opening the class.
  useEffect(() => {
    const t = setTimeout(() => setShowLaunch(false), 2400);
    return () => clearTimeout(t);
  }, []);

  // Live polling so pupil-driven changes (from their own devices) appear here.
  useEffect(() => {
    const down = () => (pointerDownRef.current = true);
    const up = () => (pointerDownRef.current = false);
    window.addEventListener("mousedown", down);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchstart", down);
    window.addEventListener("touchend", up);
    const poll = setInterval(async () => {
      if (pointerDownRef.current || document.hidden) return;
      const { data } = await fetchClass(id);
      if (data) setRoom(data);
    }, 6000);
    return () => {
      window.removeEventListener("mousedown", down);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchstart", down);
      window.removeEventListener("touchend", up);
      clearInterval(poll);
    };
  }, [id]);

  // Whole-class celebration: fire when every pupil newly passes a stage.
  useEffect(() => {
    if (!room) return;
    if (projRef.current !== room.activeProjectId) {
      projRef.current = room.activeProjectId;
      celebratedRef.current = null; // don't celebrate when switching projects
    }
    const allPassed = (idx) =>
      room.pupils.length > 0 && room.pupils.every((p) => p.stageIndex >= idx);
    const done = new Set();
    for (let i = 1; i < room.stages.length; i++) if (allPassed(i)) done.add(i);

    if (celebratedRef.current === null) {
      celebratedRef.current = done; // initialise silently on first load
      return;
    }
    for (const i of done) {
      if (!celebratedRef.current.has(i)) {
        const isFinish = i === room.stages.length - 1;
        fireCelebration(isFinish);
        playChime(muted);
        setClassBanner({ stage: room.stages[i], finish: isFinish });
        setTimeout(() => setClassBanner(null), 3400);
      }
    }
    celebratedRef.current = done;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room?.pupils, room?.stages]);

  if (!room) {
    return (
      <div className="min-h-screen bg-[#0B0D1B] text-white flex items-center justify-center">
        <p className="font-heading text-xl">Loading board…</p>
      </div>
    );
  }

  const n = room.stages.length;
  const activeProject = (room.projects || []).find((p) => p.id === room.activeProjectId);
  const accent = activeProject?.color || "#3B82F6";
  const PAD = 6;
  const stagePct = (i) => (n <= 1 ? PAD : PAD + (i / (n - 1)) * (100 - 2 * PAD));
  const allPassed = (idx) =>
    room.pupils.length > 0 && room.pupils.every((p) => p.stageIndex >= idx);

  return (
    <div
      ref={containerRef}
      data-testid="smartboard-view-container"
      className="relative h-screen bg-[#0B0D1B] text-white overflow-hidden"
    >
      <Starfield />

      <div className="relative z-10 flex flex-col h-full p-3 sm:p-5">
        {/* Top bar */}
        <header className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              data-testid="nav-home-link"
              onClick={() => navigate("/")}
              variant="ghost"
              size="icon"
              className="text-slate-300 hover:text-white hover:bg-[#1C204B] shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0">
              <h1 className="font-heading text-xl sm:text-3xl font-bold truncate">{room.name}</h1>
              {room.teacherName && (
                <p data-testid="board-teacher-name" className="text-xs sm:text-sm text-amber-300 font-heading -mt-0.5 truncate">
                  👑 {(room.teachers?.length ? room.teachers.map((t) => t.name).join(" & ") : room.teacherName)}'s class
                </p>
              )}
            </div>
            <span
              data-testid="offline-status-badge"
              className={`hidden sm:flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                offline
                  ? "bg-orange-500/20 text-orange-300"
                  : "bg-emerald-500/20 text-emerald-300"
              }`}
            >
              {offline ? <WifiOff className="w-3 h-3" /> : <Wifi className="w-3 h-3" />}
              {offline ? "Offline" : "Synced"}
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              data-testid="share-code-btn"
              onClick={() => setShareOpen(true)}
              className="bg-[#141738] hover:bg-[#1C204B] border border-cyan-400/40 text-cyan-200 font-heading font-bold min-h-[44px] tracking-widest"
              title="Show the join code for pupils"
            >
              <Share2 className="w-4 h-4 mr-2" />
              <span data-testid="join-code-inline">{room.code || "…"}</span>
            </Button>
            <Button
              data-testid="sound-toggle-btn"
              onClick={() => setMuted((m) => !m)}
              variant="outline"
              size="icon"
              className="border-[#252B5C] bg-transparent text-white hover:bg-[#1C204B]"
            >
              {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </Button>
            <Button
              data-testid="smartboard-fullscreen-btn"
              onClick={toggleFullscreen}
              variant="outline"
              size="icon"
              className="border-[#252B5C] bg-transparent text-white hover:bg-[#1C204B]"
            >
              {fullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
            </Button>
            <Button
              data-testid="teacher-tools-btn"
              onClick={() => setTools(true)}
              className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600 text-white font-heading font-semibold min-h-[44px]"
            >
              <Settings2 className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Mission Control</span>
            </Button>
          </div>
        </header>

        {/* Project switcher */}
        <div data-testid="project-bar" className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-xs uppercase tracking-wider text-cyan-400 font-semibold">Project</span>
          <select
            data-testid="project-select"
            value={room.activeProjectId}
            onChange={(e) => switchProject(e.target.value)}
            className="bg-[#141738] border border-[#252B5C] rounded-md h-9 px-2 text-white max-w-[220px] font-heading"
          >
            {(room.projects || []).map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <div className="relative ml-auto">
            <Button
              data-testid="teachers-tech-btn"
              onClick={() => setTechOpen((o) => !o)}
              size="sm"
              variant="outline"
              className="border-amber-400/40 bg-transparent text-amber-200 hover:bg-amber-400/10"
              title="Teacher-only tools (hidden from the class view)"
            >
              <Wrench className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Teachers Tech</span>
            </Button>
            {techOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setTechOpen(false)} />
                <div data-testid="teachers-tech-menu" className="absolute right-0 top-full mt-1 z-40 w-52 bg-[#141738] border border-[#252B5C] rounded-xl shadow-2xl p-1.5 space-y-1">
                  <button data-testid="new-project-btn" onClick={() => { setTechOpen(false); newProject(); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white hover:bg-[#1C204B]">
                    <FolderPlus className="w-4 h-4 text-cyan-300" /> New project
                  </button>
                  <button data-testid="timeline-btn" onClick={() => { setTechOpen(false); setTimelineOpen(true); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white hover:bg-[#1C204B]">
                    <CalendarClock className="w-4 h-4 text-cyan-300" /> Timeline
                  </button>
                  <button data-testid="tracker-btn" onClick={() => { setTechOpen(false); setTrackerOpen(true); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white hover:bg-[#1C204B]">
                    <ListChecks className="w-4 h-4 text-cyan-300" /> Tracker
                  </button>
                  <button data-testid="report-btn" onClick={() => { setTechOpen(false); setReportOpen(true); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white hover:bg-[#1C204B]">
                    <FileText className="w-4 h-4 text-cyan-300" /> Report
                  </button>
                  <button data-testid="teachers-manage-btn" onClick={() => { setTechOpen(false); setTeachersOpen(true); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white hover:bg-[#1C204B]">
                    <Users className="w-4 h-4 text-amber-300" /> Co-teachers
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Stage header (finish lines) */}
        <div
          data-testid="stage-header-list"
          className="flex items-center gap-3 sm:gap-4 mb-4"
        >
          <div className="w-24 sm:w-36 shrink-0" />
          <div className="relative flex-1 h-16 sm:h-20">
            {room.stages.map((s, i) => (
              <div
                key={i}
                data-testid={`stage-marker-${i}`}
                className="absolute top-0 flex flex-col items-center"
                style={{ left: `calc(${stagePct(i)}% )`, transform: "translateX(-50%)" }}
              >
                <div
                  className={`relative ${allPassed(i) && i > 0 ? "planet-pulse" : ""}`}
                  style={{
                    filter:
                      allPassed(i) && i > 0
                        ? `drop-shadow(0 0 14px ${s.color}) drop-shadow(0 0 6px ${s.color})`
                        : "none",
                  }}
                >
                  <Planet index={i} total={n} color={accent} planet={s.planet} size={i === 0 || i === n - 1 ? 72 : 60 + ((i * 7) % 16)} />
                  {allPassed(i) && i > 0 && (
                    <span
                      data-testid={`stage-flag-${i}`}
                      className="flag-pop absolute -top-2 -right-1 text-xl sm:text-2xl"
                      title="Whole class conquered this planet!"
                    >
                      <span className="flag-wave inline-block">🚩</span>
                    </span>
                  )}
                </div>
                <span className="mt-1 text-[10px] sm:text-xs font-heading font-semibold text-center max-w-[80px] sm:max-w-[110px] leading-tight text-slate-200 truncate-2">
                  {s.name}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Pupil lanes */}
        <div
          data-testid="pupil-lane-list"
          className="flex-1 min-h-0 space-y-3 sm:space-y-4 overflow-y-auto pb-24 pt-8"
        >
          {room.pupils.length === 0 ? (
            <div className="text-center py-16">
              <div className="rocket-float inline-block mb-3">
                <Rocket color="#A855F7" size={80} />
              </div>
              <p className="font-heading text-lg text-slate-300">No astronauts on board yet.</p>
              <Button
                onClick={() => setTools(true)}
                className="mt-3 bg-pink-500 hover:bg-pink-600 min-h-[44px]"
                data-testid="empty-add-pupil-btn"
              >
                Add pupils in Mission Control
              </Button>
            </div>
          ) : (
            room.pupils.map((p) => (
              <PupilLane
                key={p.id}
                pupil={p}
                stages={room.stages}
                onSnap={onSnap}
                onOpenHistory={setHistoryPupil}
              />
            ))
          )}
        </div>

        <p className="text-center text-xs text-slate-500 pt-2">
          Drag a rocket along its lane — it will snap to the nearest planet. 🚀
        </p>
      </div>

      {/* Celebration banner */}
      {banner && (
        <div
          data-testid="celebration-dialog"
          className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
        >
          <div className="celebrate-pop bg-[#141738]/95 backdrop-blur-xl border-2 rounded-3xl px-8 py-6 text-center shadow-2xl"
            style={{ borderColor: banner.color, boxShadow: `0 0 40px ${banner.color}88` }}
          >
            <div className="text-5xl mb-2">{banner.finish ? "🏆" : banner.stage.icon}</div>
            <p className="font-heading text-2xl sm:text-3xl font-bold" style={{ color: banner.color }}>
              {banner.name}
            </p>
            <p className="font-heading text-lg text-white mt-1">
              {banner.finish ? "completed the mission!" : `reached ${banner.stage.name}!`}
            </p>
          </div>
        </div>
      )}

      {/* Whole-class celebration banner */}
      {classBanner && (
        <div
          data-testid="class-celebration-dialog"
          className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-none"
        >
          <div
            className="celebrate-pop bg-[#141738]/95 backdrop-blur-xl border-2 rounded-3xl px-10 py-8 text-center shadow-2xl"
            style={{ borderColor: classBanner.stage.color, boxShadow: `0 0 60px ${classBanner.stage.color}` }}
          >
            <div className="text-6xl mb-2">
              {classBanner.finish ? "🏆🎉" : `${classBanner.stage.icon}🚩`}
            </div>
            <p className="font-heading text-3xl sm:text-4xl font-bold" style={{ color: classBanner.stage.color }}>
              Whole class!
            </p>
            <p className="font-heading text-lg sm:text-xl text-white mt-1">
              {classBanner.finish
                ? "Everyone completed the mission! 🚀"
                : `Everyone conquered ${classBanner.stage.name}!`}
            </p>
          </div>
        </div>
      )}

      {/* Pupil progress history */}
      <Dialog open={!!historyPupil} onOpenChange={(o) => !o && setHistoryPupil(null)}>
        <DialogContent
          data-testid="pupil-history-dialog"
          className="bg-[#141738] border-[#252B5C] text-white max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <CalendarDays className="w-6 h-6 text-cyan-300" />
              {historyPupil?.name}'s Journey
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              The date this astronaut first reached each mission stage.
            </DialogDescription>
          </DialogHeader>
          {historyPupil && (
            <div className="space-y-2 pt-2">
              {room.stages.map((s, i) => {
                if (i === 0) return null;
                const entry = (historyPupil.history || []).find((h) => h.stageIndex === i);
                const reached = historyPupil.stageIndex >= i;
                return (
                  <div
                    key={i}
                    data-testid={`history-stage-${i}`}
                    className={`flex items-center gap-3 rounded-xl p-3 border ${
                      reached
                        ? "bg-[#0E1126] border-[#252B5C]"
                        : "bg-transparent border-[#252B5C]/40 opacity-50"
                    }`}
                  >
                    <span
                      className="w-9 h-9 rounded-full flex items-center justify-center text-lg border"
                      style={{ borderColor: s.color, backgroundColor: `${s.color}22` }}
                    >
                      {s.icon}
                    </span>
                    <span className="font-heading font-semibold flex-1 truncate">{s.name}</span>
                    {entry ? (
                      <span className="text-sm text-emerald-300 font-semibold">
                        {new Date(entry.date + "T00:00:00").toLocaleDateString(undefined, {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                    ) : reached ? (
                      <span className="text-sm text-slate-400">reached</span>
                    ) : (
                      <span className="text-sm text-slate-500">not yet</span>
                    )}
                  </div>
                );
              })}
              {(historyPupil.history || []).length === 0 &&
                historyPupil.stageIndex === 0 && (
                  <p className="text-slate-400 text-sm text-center py-4">
                    No stages reached yet — this astronaut is still on the launchpad. 🚀
                  </p>
                )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Rocket lift-off intro */}
      {showLaunch && (
        <div
          data-testid="launch-intro"
          className="launch-overlay fixed inset-0 z-[70] flex flex-col items-center justify-end bg-[#0B0D1B] overflow-hidden"
        >
          <Starfield />
          <div className="relative z-10 launch-rise">
            <div className="launch-shake">
              <Rocket color="#00F0FF" size={120} />
            </div>
          </div>
          <p className="absolute top-1/3 z-10 font-heading text-4xl sm:text-6xl font-bold text-white text-center w-full">
            Blast off! 🚀
          </p>
        </div>
      )}

      {/* Share join code */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent data-testid="join-code-overlay" className="bg-[#141738] border-[#252B5C] text-white max-w-lg text-center">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-cyan-300">Pupils, join this mission!</DialogTitle>
            <DialogDescription className="text-slate-400">
              On any device (Chromebook, iPad, phone) open the site and enter this code.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm text-slate-400 mb-1">On any device (Chromebook, iPad, phone), go to</p>
            <p className="font-heading text-2xl text-white mb-4">
              <span className="text-cyan-300">{JOIN_HOST}</span>
            </p>
            <p className="text-sm text-slate-400 mb-2">and type the code</p>
            <div
              data-testid="big-join-code"
              className="font-heading font-bold text-6xl sm:text-7xl tracking-[0.3em] text-cyan-300 bg-[#0E1126] border-2 border-cyan-400/40 rounded-2xl py-6"
            >
              {room.code}
            </div>
            <Button
              data-testid="copy-join-link-btn"
              onClick={() => {
                navigator.clipboard?.writeText(joinUrl());
                toast.success("Join link copied!");
              }}
              variant="outline"
              className="mt-4 border-[#252B5C] bg-transparent text-white hover:bg-[#1C204B] min-h-[44px]"
            >
              <Copy className="w-4 h-4 mr-2" /> Copy join link
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <TimelineDialog open={timelineOpen} onOpenChange={setTimelineOpen} room={room} />
      <TrackerDialog open={trackerOpen} onOpenChange={setTrackerOpen} room={room} />
      <ClassReportDialog open={reportOpen} onOpenChange={setReportOpen} room={room} onRoom={setRoom} />

      <TeacherTools open={tools} onOpenChange={setTools} room={room} onChange={saveRoom} onRoom={setRoom} />

      {/* Teachers & PINs — private teacher-only management (hidden from the class) */}
      <Dialog open={teachersOpen} onOpenChange={setTeachersOpen}>
        <DialogContent data-testid="teachers-dialog" className="bg-[#141738] border-[#252B5C] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-amber-300 flex items-center gap-2">
              <Users className="w-6 h-6" /> Co-teachers
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Invite colleagues to co-teach this class, or remove them. Each teacher signs in with their own code.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <div>
              <p className="font-heading font-semibold text-amber-300 mb-1 flex items-center gap-2">
                <UserPlus className="w-4 h-4" /> Invite another teacher
              </p>
              <p className="text-xs text-slate-400 mb-2">
                One-time link — a colleague enters their name, gets their own teacher code, and becomes a co-teacher.
              </p>
              <Button
                data-testid="gen-teacher-invite-btn"
                onClick={genInvite}
                disabled={inviteBusy}
                variant="outline"
                className="w-full border-amber-400/40 bg-transparent text-amber-200 hover:bg-amber-400/10 min-h-[44px]"
              >
                <Copy className="w-4 h-4 mr-2" /> {inviteBusy ? "Creating…" : "Create one-time invite link"}
              </Button>
              {inviteLink && (
                <div className="mt-2 text-xs bg-[#0E1126] border border-[#252B5C] rounded-lg p-2 break-all">
                  <span data-testid="teacher-invite-url" className="text-cyan-200">{inviteLink}</span>
                  <span className="block text-slate-500 mt-1">Copied to clipboard · single-use, valid 72 hours.</span>
                </div>
              )}
            </div>

            <div className="space-y-2 border-t border-[#252B5C] pt-3">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Teachers</p>
              {(room.teachers || []).map((t, idx) => (
                <div key={t.id} data-testid={`teacher-row-${t.id}`} className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-slate-300 flex-1 min-w-0 truncate">
                    👑 {t.name}
                    {idx === 0 && <span className="text-[10px] text-slate-500 ml-1">(main)</span>}
                  </span>
                  {idx > 0 && (
                    <Button
                      data-testid={`remove-teacher-btn-${t.id}`}
                      onClick={() => removeCoTeacher(t.id)}
                      size="sm"
                      variant="outline"
                      className="h-7 border-red-400/40 bg-transparent text-red-300 hover:bg-red-500/10 text-xs shrink-0"
                    >
                      <UserMinus className="w-3 h-3 mr-1" /> Remove
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bottom-centre holographic clock, flanked by teacher presentation tools */}
      <ClockDock
        accent={accent}
        left={
          <Button
            data-testid="timer-open-btn"
            onClick={() => setTimerOpen(true)}
            className="h-12 rounded-2xl font-heading font-bold text-[#0B0D1B] shadow-lg"
            style={{ backgroundColor: accent }}
          >
            <Timer className="w-5 h-5 sm:mr-1" /><span className="hidden sm:inline">Timer</span>
          </Button>
        }
        right={
          <Button
            data-testid="whiteboard-open-btn"
            onClick={() => setWhiteboardOpen(true)}
            variant="outline"
            className="h-12 rounded-2xl border-2 bg-[#0E1126]/80 text-white font-heading font-bold"
            style={{ borderColor: accent }}
          >
            <PenTool className="w-5 h-5 sm:mr-1" /><span className="hidden sm:inline">Whiteboard</span>
          </Button>
        }
      />
      <MissionTimer open={timerOpen} onClose={() => setTimerOpen(false)} accent={accent} />
      <Whiteboard open={whiteboardOpen} onClose={() => setWhiteboardOpen(false)} room={room} onRoom={setRoom} accent={accent} />
    </div>
  );
}
